import React from 'react';
import { observer } from 'mobx-react-lite';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import globalUserProfile from '../../../../stores/global-user-profile';
import { StudentProfile } from '../../../../api/types/settings/settings-all';
import { LectureDetailsStudentWithCourseDetails } from '../../../../api/types/students/lectures/student-lectures';

export const MyLecturesStudent = observer(() => {
  // Check if user profile exists and is a student
  if (!globalUserProfile.userProfile?.profile) {
    return <div>Loading user profile...</div>;
  }

  const studentProfile = globalUserProfile.userProfile
    .profile as StudentProfile;
  const lectures: LectureDetailsStudentWithCourseDetails[] =
    studentProfile.lectures;

  if (!lectures || lectures.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>ההרצאות השבוע</h2>
        <p>לא נמצאו הרצאות רשומות</p>
      </div>
    );
  }

  // Template functions for DataTable columns
  const attendanceStatusTemplate = (
    lecture: LectureDetailsStudentWithCourseDetails,
  ) => {
    if (!lecture.attendanceType) {
      return (
        <Tag
          style={{ width: '70px' }}
          value="טרם הוזן"
          className="neutral-tag"
          severity={'neutral'}
        />
      );
    }

    switch (lecture.attendanceType) {
      case 'attended':
        return <Tag style={{ width: '70px' }} value="נכח" severity="success" />;
      case 'late':
        return (
          <Tag style={{ width: '70px' }} value="איחור" severity="warning" />
        );
      case 'missed':
        return (
          <Tag style={{ width: '70px' }} value="לא נכח" severity="danger" />
        );
      default:
        return (
          <Tag style={{ width: '50px' }} value="לא ידוע" severity="secondary" />
        );
    }
  };

  const scheduleTypeTemplate = (
    lecture: LectureDetailsStudentWithCourseDetails,
  ) => {
    if (!lecture.scheduleType) return '-';
    return lecture.scheduleType === 'lecture' ? 'הרצאה' : 'בחינה';
  };

  const attendanceRequiredTemplate = (
    lecture: LectureDetailsStudentWithCourseDetails,
  ) => {
    return lecture.attendanceRequired ? 'כן' : 'לא';
  };

  const timeTemplate = (lecture: LectureDetailsStudentWithCourseDetails) => {
    if (!lecture.startTime || !lecture.endTime) return '-';

    const startTime = new Date(lecture.startTime).toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
    });
    const endTime = new Date(lecture.endTime).toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${startTime} - ${endTime}`;
  };

  const dateTemplate = (lecture: LectureDetailsStudentWithCourseDetails) => {
    if (!lecture.startTime) return '-';
    return new Date(lecture.startTime).toLocaleDateString('he-IL');
  };

  const locationTemplate = (
    lecture: LectureDetailsStudentWithCourseDetails,
  ) => {
    const parts = [];
    if (lecture.buildingName) parts.push(lecture.buildingName);
    if (lecture.className) parts.push(lecture.className);
    return parts.length > 0 ? parts.join(' - ') : '-';
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2>ההרצאות השבוע</h2>
      <DataTable
        value={lectures}
        stripedRows
        showGridlines
        tableStyle={{ minWidth: '50rem' }}
        rows={10}
        style={{ marginTop: '16px' }}
      >
        <Column
          field="lectureName"
          header="שם ההרצאה"
          style={{ textAlign: 'right', fontWeight: 'bold' }}
        />
        <Column
          field="courseName"
          header="קורס"
          style={{ textAlign: 'right' }}
        />
        <Column
          field="programName"
          header="תוכנית לימודים"
          style={{ textAlign: 'right' }}
        />
        <Column
          header="תאריך"
          body={dateTemplate}
          style={{ textAlign: 'right' }}
        />
        <Column
          header="שעות"
          body={timeTemplate}
          style={{ textAlign: 'right' }}
        />
        <Column
          header="מיקום"
          body={locationTemplate}
          style={{ textAlign: 'right' }}
        />
        <Column
          header="סוג"
          body={scheduleTypeTemplate}
          style={{ textAlign: 'right' }}
        />
        <Column
          header="נוכחות נדרשת"
          body={attendanceRequiredTemplate}
          style={{ textAlign: 'right' }}
        />
        <Column
          header="סטטוס נוכחות"
          body={attendanceStatusTemplate}
          style={{ textAlign: 'right' }}
        />
      </DataTable>
    </div>
  );
});
